import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Button from '../../../base/ui/components/web/Button';
import Spinner from '../../../base/ui/components/web/Spinner';
import { BUTTON_TYPES } from '../../../base/ui/constants.web';
import { discoverAgent } from '../../actions';
import { AgentStatus } from '../../types';
const useStyles = makeStyles()(theme => {
    return {
        body: {
            ...theme.typography.bodyLongRegular,
            color: theme.palette.text01,
            maxWidth: '52ch'
        },
        diagnostic: {
            ...theme.typography.bodyShortRegular,
            color: theme.palette.text01,
            margin: `${theme.spacing(3)} 0`,
            padding: theme.spacing(3),
            borderRadius: '8px',
            borderLeft: `3px solid ${theme.palette.warning01}`,
            background: `${theme.palette.ui02}`,
            maxWidth: '52ch'
        },
        actions: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            marginTop: theme.spacing(4)
        },
        note: {
            ...theme.typography.labelRegular,
            color: theme.palette.text02,
            marginTop: theme.spacing(3),
            maxWidth: '52ch'
        },
        watching: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            marginTop: theme.spacing(4),
            ...theme.typography.bodyShortRegular,
            color: theme.palette.text02
        }
    };
});
/**
 * How often to look again for a helper that is being installed right now.
 */
const RETRY_INTERVAL = 3000;
/**
 * Guesses which build of the helper this computer needs, so the download button
 * can be one click rather than a table of platforms.
 *
 * @returns {string}
 */
function guessPlatform() {
    const platform = `${navigator.userAgent} ${navigator.platform}`.toLowerCase();
    if (platform.includes('win')) {
        return 'windows';
    }
    if (platform.includes('mac')) {
        return 'macos';
    }
    if (platform.includes('linux') || platform.includes('x11')) {
        return 'linux';
    }
    return '';
}
/**
 * Explains why this computer cannot be controlled yet, and offers the one thing
 * that would fix it.
 *
 * There are two quite different reasons to be here — the helper is not
 * installed, or it is installed and lacks a permission — and they need
 * different answers, so neither is softened into a general "something went
 * wrong".
 *
 * @returns {ReactElement}
 */
const AgentSetup = () => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { hello, status } = useSelector((state) => state['features/remote-control'].agent);
    // The install page picks the platform up from the fragment, so the guess
    // made here only saves a click and is never load-bearing.
    const downloadUrl = useMemo(() => {
        const platform = hello?.os === 'darwin' ? 'macos' : hello?.os ?? guessPlatform();
        return platform ? `/agent/#${platform}` : '/agent/';
    }, [hello]);
    const onDownload = useCallback(() => {
        window.open(downloadUrl, '_blank', 'noopener');
    }, [downloadUrl]);
    // Keep looking while this is on screen. Someone who installs the helper
    // now should not have to work out that they need to rejoin the meeting —
    // the dialog turns into the ordinary request the moment it appears.
    useEffect(() => {
        const timer = window.setInterval(() => {
            dispatch(discoverAgent());
        }, RETRY_INTERVAL);
        return () => window.clearInterval(timer);
    }, [dispatch]);
    if (status === AgentStatus.NotReady) {
        return (React.createElement("div", null,
            React.createElement("p", { className: classes.body }, t('remoteControl.setupNotReady')),
            hello?.diagnostic && (React.createElement("p", { className: classes.diagnostic }, hello.diagnostic)),
            React.createElement("p", { className: classes.note }, t('remoteControl.setupRestart'))));
    }
    return (React.createElement("div", null,
        React.createElement("p", { className: classes.body }, t('remoteControl.setupBody')),
        React.createElement("div", { className: classes.actions },
            React.createElement(Button, { accessibilityLabel: t('remoteControl.setupDownload'), labelKey: 'remoteControl.setupDownload', onClick: onDownload, type: BUTTON_TYPES.PRIMARY })),
        React.createElement("p", { className: classes.note }, t('remoteControl.setupPrivacy')),
        React.createElement("div", { className: classes.watching },
            React.createElement(Spinner, { size: 'small' }),
            React.createElement("span", null, t('remoteControl.setupWatching')))));
};
export default AgentSetup;
